import { useState, useMemo } from 'react'
import { Box, styled } from '@mui/material'
import { FormattedMessage } from 'react-intl'

import { Map } from 'features/maps/types'
import useMapSharingInfo from 'features/maps/state/useMapSharingInfo'

import { mapHasUpdatePending } from 'features/maps/utils'
import { DomainOutlined, LockOutlined, PublicOutlined } from '@mui/icons-material'
import BuilderShareActionsMenu from './BuilderShareActionsMenu'
import HeaderSplitButton from 'features/common/ui/SplitButton/HeaderSplitButton'
import { useAppSelector } from 'app/state'
import { createDialogOpener } from 'features/dialogs/openDialog'

const openBuilderShareDialog = createDialogOpener(
  () => import('features/builder/ui/BuilderPrivate/BuilderShareDialog/BuilderShareDialog')
)

const ShareButtonWrapper = styled('div')(() => ({
  display: 'flex',
  position: 'relative',
  flexDirection: 'row'
}))

const Circle = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: 0,
  right: 0,
  transform: 'translate(calc(50% - 1px), calc(-50% + 1px))',
  width: theme.spacing(1),
  height: theme.spacing(1),
  backgroundColor: theme.palette.secondary.main,
  border: `1px solid ${theme.palette.brand.appBarMain}`,
  borderRadius: '50%'
}))

function BuilderShareDialogButton({ currentMap }: { currentMap?: Map }) {
  const [showShareTooltip, setShowShareTooltip] = useState<boolean>(false)

  const publishMapUpdatePending = currentMap && mapHasUpdatePending(currentMap)
  const { isSharedMap, isPublicMap } = useMapSharingInfo(currentMap)

  const isPrivateMap = !isSharedMap && !isPublicMap
  const currentMapDatasets = useAppSelector((state) => state.maps.currentMapDatasets)
  const isReadyToShare = Boolean(currentMap?.title)
  const disableSharingButton = isPrivateMap && !isReadyToShare
  const isCta = currentMapDatasets.length > 0 && !disableSharingButton

  const [StartIcon, shareTooltipStatusId] = useMemo(() => {
    if (isPublicMap) {
      return [PublicOutlined, 'builder.navBar.sharePublicTooltip']
    }
    if (isSharedMap) {
      return [DomainOutlined, 'builder.navBar.shareSharedTooltip']
    }
    return [LockOutlined, 'builder.navBar.sharePrivateTooltip']
  }, [isPublicMap, isSharedMap])

  const openShareDialog = () => {
    if (!currentMap) return

    openBuilderShareDialog({
      mapId: currentMap.id,
      showPublishingStatus: true
    })

    setShowShareTooltip(false)
  }

  const handleSecondaryClick = () => {
    setShowShareTooltip(false)
  }

  return (
    <Box>
      <ShareButtonWrapper
        onMouseEnter={() => {
          setShowShareTooltip(true)
        }}
        onMouseLeave={() => {
          setShowShareTooltip(false)
        }}
      >
        <HeaderSplitButton
          variant={isCta ? 'contained' : 'outlined'}
          color={isCta ? 'primary' : 'inherit'}
          label={<FormattedMessage id="common.actions.share" />}
          onMainClick={openShareDialog}
          onSecondaryClick={handleSecondaryClick}
          tooltip={
            showShareTooltip &&
            (disableSharingButton ? (
              <FormattedMessage id="builder.navBar.shareDisabledTooltip" />
            ) : (
              <FormattedMessage id={shareTooltipStatusId} />
            ))
          }
          MainButtonProps={{
            'aria-label': 'share-map',
            startIcon: <StartIcon />,
            disabled: disableSharingButton
          }}
          SecondButtonProps={{
            'aria-label': 'sharing-menu'
          }}
        >
          {(dismissButtonMenu) => (
            <BuilderShareActionsMenu
              onDismiss={dismissButtonMenu}
              onOpenSharingSettings={openShareDialog}
              sharingDisabled={disableSharingButton}
            />
          )}
        </HeaderSplitButton>
        {publishMapUpdatePending && !disableSharingButton && <Circle aria-label="update-pending" />}
      </ShareButtonWrapper>
    </Box>
  )
}

export default BuilderShareDialogButton
